import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import {
  Flex,
  Typography,
  Card,
  Divider,
  Badge,
  Row,
  Col,
  message,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThemedChip from "../../common/ThemedChip";
import { downloadWhitesheet } from "../../../utils/apiClient";

const { Text, Title } = Typography;

/**
 * CallSummaryView - Shows whitesheet data with call details and detailed breakdown
 * EXACT copy from CallSummaryNew.js for Call History V2
 */
const CallSummaryView = ({ callData, whitesheetData, patientData, currentTheme, themeMode }) => {
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    callDetails: true,
    detailedBreakdown: true,
    memberDetails: true,
  });

  const isDark = themeMode === "dark";

  // Helper function to filter out NA values from data
  const filterNAValues = (data) => {
    if (!data) return null;
    
    if (Array.isArray(data)) {
      return data.map(item => filterNAValues(item)).filter(item => item !== null);
    }
    
    if (typeof data === "object") {
      const filtered = {};
      Object.entries(data).forEach(([key, value]) => {
        if (value !== "NA" && value !== "N/A" && value !== null && value !== undefined && value !== "") {
          filtered[key] = value;
        }
      });
      return Object.keys(filtered).length > 0 ? filtered : null;
    }
    
    if (data === "NA" || data === "N/A" || data === "" || data === null || data === undefined) {
      return null;
    }
    
    return data;
  };

  // Extract whitesheet data properly
  const whitesheetFinal = whitesheetData?.final || whitesheetData?.predicted || {};
  
  const callSummaryData = {
    call_details: filterNAValues(whitesheetFinal.call_details),
    detailed_breakdown: filterNAValues(whitesheetFinal.detailed_breakdown),
    member_details: filterNAValues(whitesheetFinal.member_details),
  };

  console.log("callSummaryData", callSummaryData);
  
  // Check if whitesheet is available
  const hasData =
    callSummaryData.call_details ||
    callSummaryData.detailed_breakdown ||
    callSummaryData.member_details;
  
  // Check if verified
  const isVerified = whitesheetData?.reviewed || false;

  // Get call request ID for downloads
  const callRequestId = callData?.callRequestId;

  // Helper function to safely convert any value to displayable string
  const safeValueToString = (value) => {
    if (value === null || value === undefined) {
      return "Not Available";
    }

    if (value === "NA" || value === "N/A" || value === "") {
      return "Not Available";
    }

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "Not Available";
      return value.map((item) => safeValueToString(item)).join(", ");
    }

    if (typeof value === "object") {
      try {
        const entries = Object.entries(value);
        if (entries.length === 0) return "Not Available";
        if (entries.length <= 3) {
          return entries
            .map(([k, v]) => `${k}: ${safeValueToString(v)}`)
            .join(", ");
        }
        return `Object with ${entries.length} properties`;
      } catch (e) {
        return "Complex data";
      }
    }

    return String(value);
  };

  // Helper function to format field names
  const formatFieldName = (key) => {
    if (!key || typeof key !== "string") return "Unknown Field";
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to get status color and icon
  const getStatusStyling = (key, value) => {
    const stringValue = safeValueToString(value);
    const isNA = stringValue === "Not Available";
    const isActive = stringValue !== "Not Available" && stringValue !== "NA";

    // Special handling for call status types
    const statusTypes = {
      plan_termination: {
        color: "#ff4d4f",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      member_not_found: {
        color: "#faad14",
        icon: <WarningOutlined />,
        severity: "medium",
      },
      system_issue: {
        color: "#ff7a45",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      restricted_plan: {
        color: "#ff4d4f",
        icon: <WarningOutlined />,
        severity: "high",
      },
      outoff_office: {
        color: "#faad14",
        icon: <ClockCircleOutlined />,
        severity: "medium",
      },
      robocalls: {
        color: "#ff4d4f",
        icon: <PhoneOutlined />,
        severity: "high",
      },
      call_queue_disconnection: {
        color: "#faad14",
        icon: <PhoneOutlined />,
        severity: "medium",
      },
      representative_holdtimeout: {
        color: "#faad14",
        icon: <ClockCircleOutlined />,
        severity: "medium",
      },
      ivr_disconnection: {
        color: "#faad14",
        icon: <PhoneOutlined />,
        severity: "medium",
      },
      outoff_network: {
        color: "#ff7a45",
        icon: <ExclamationCircleOutlined />,
        severity: "high",
      },
      call_status: {
        color: "#52c41a",
        icon: <CheckCircleOutlined />,
        severity: "low",
      },
    };

    // Special handling for call_status - check if denied/Denial (including "Claim Denied" etc.)
    if (key === "call_status") {
      const lowerValue = stringValue.toLowerCase();
      if (lowerValue.includes("denied") || lowerValue.includes("denial")) {
        return {
          color: "#ff4d4f",
          backgroundColor: "rgba(255, 77, 79, 0.08)",
          borderColor: "rgba(255, 77, 79, 0.2)",
          icon: <ExclamationCircleOutlined />,
          severity: "high",
          isActive: true,
        };
      }
    }

    const statusInfo = statusTypes[key] || {
      color: isDark ? "#fff" : "#333",
      icon: null,
      severity: "low",
    };

    return {
      color: isNA
        ? isDark
          ? "#666"
          : "#999"
        : isActive
        ? statusInfo.color
        : isDark
        ? "#fff"
        : "#333",
      backgroundColor:
        isActive && !isNA ? `${statusInfo.color}15` : "transparent",
      borderColor:
        isActive && !isNA
          ? `${statusInfo.color}30`
          : isDark
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)",
      icon: isActive && !isNA ? statusInfo.icon : null,
      severity: statusInfo.severity,
      isActive: isActive && !isNA,
    };
  };

  // Render individual field with enhanced styling
  const renderField = (key, value, isNested = false) => {
    const styling = getStatusStyling(key, value);
    const displayValue = safeValueToString(value);
    const isHighlighted = styling.isActive;

    return (
      <div
        key={key}
        style={{
          padding: "1rem 1.25rem",
          paddingLeft: isNested ? "2rem" : "1.25rem",
          backgroundColor: styling.backgroundColor,
          borderRadius: "0.5rem",
          margin: "0.375rem 0",
          border: `1px solid ${styling.borderColor}`,
          transition: "all 0.3s ease",
          boxShadow: isHighlighted ? `0 0.125rem 0.5rem ${styling.color}20` : "none",
        }}
      >
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8} style={{ width: "60%" }}>
            {styling.icon && (
              <span style={{ color: styling.color, fontSize: "0.875rem" }}>
                {styling.icon}
              </span>
            )}
            <Text
              style={{
                color: styling.color,
                fontSize: "0.9375rem",
                fontWeight: isHighlighted ? "600" : "500",
                lineHeight: "1.4",
              }}
            >
              {formatFieldName(key)}
            </Text>
            {isHighlighted && styling.severity === "high" && (
              <Badge status="error" style={{ marginLeft: "0.25rem" }} />
            )}
            {isHighlighted && styling.severity === "medium" && (
              <Badge status="warning" style={{ marginLeft: "0.25rem" }} />
            )}
          </Flex>
          <div style={{ width: "40%", textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            {isHighlighted ? (
              <ThemedChip
                variant={
                  styling.severity === "high"
                    ? "error"
                    : styling.severity === "medium"
                    ? "warning"
                    : "success"
                }
                size="small"
              >
                {displayValue}
              </ThemedChip>
            ) : (
              <Text
                style={{
                  color: styling.color,
                  fontSize: "0.875rem",
                  fontStyle:
                    displayValue === "Not Available" ? "italic" : "normal",
                  lineHeight: "1.4",
                }}
              >
                {displayValue}
              </Text>
            )}
          </div>
        </Flex>
      </div>
    );
  };

  // Render section with improved design
  const renderSection = (title, data, sectionKey, icon, description) => {
    if (!data) return null;

    const isExpanded = expandedSections[sectionKey];
    const toggleSection = () => {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: !prev[sectionKey],
      }));
    };

    return (
      <Card
        style={{
          backgroundColor: isDark ? "#04002b" : "#ffffff",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
          }`,
          borderRadius: "0.75rem",
          marginBottom: "1.25rem",
          overflow: "hidden",
          boxShadow: isDark
            ? "0 0.25rem 0.75rem rgba(0, 0, 0, 0.3)"
            : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Section Header */}
        <div
          onClick={toggleSection}
          style={{
            padding: "1.25rem 1.5rem",
            backgroundColor: isDark ? "#242243" : "#fafafa",
            borderBottom: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
            }`,
            cursor: "pointer",
            transition: "background-color 0.2s ease",
          }}
        >
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={16}>
              <div
                style={{
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                  backgroundColor: `${currentTheme.loginPage.loginButtonColor}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(icon, {
                  style: {
                    color: currentTheme.loginPage.loginButtonColor,
                    fontSize: "1.125rem",
                  },
                })}
              </div>
              <div>
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    color: isDark ? "#fff" : "#333",
                    fontSize: "1.125rem",
                    marginBottom: "0.25rem",
                  }}
                >
                  {title}
                </Title>
                {description && (
                  <Text
                    style={{
                      color: isDark ? "#ccc" : "#666",
                      fontSize: "0.75rem",
                    }}
                  >
                    {description}
                  </Text>
                )}
              </div>
            </Flex>
            <div
              style={{
                padding: "0.25rem 0.5rem",
                borderRadius: "0.375rem",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.04)",
              }}
            >
              {isExpanded ? (
                <EyeInvisibleOutlined
                  style={{ color: isDark ? "#fff" : "#333", fontSize: "1rem" }}
                />
              ) : (
                <EyeOutlined
                  style={{ color: isDark ? "#fff" : "#333", fontSize: "1rem" }}
                />
              )}
            </div>
          </Flex>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div style={{ padding: "1.5rem" }}>
            {Array.isArray(data) ? (
              // Handle array data (detailed_breakdown)
              data.map((item, index) => (
                <div
                  key={index}
                  style={{ marginBottom: index < data.length - 1 ? "1.5rem" : 0 }}
                >
                  {data.length > 1 && (
                    <div
                      style={{
                        marginBottom: "1rem",
                        padding: "0.5rem 1rem",
                        backgroundColor: isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                        borderRadius: "0.375rem",
                        borderLeft: `3px solid ${currentTheme.loginPage.loginButtonColor}`,
                      }}
                    >
                      <Text
                        style={{
                          color: isDark ? "#ccc" : "#666",
                          fontSize: "0.8125rem",
                          fontWeight: "600",
                        }}
                      >
                        Record {index + 1}
                      </Text>
                    </div>
                  )}
                  {typeof item === "object"
                    ? Object.entries(item).map(([key, value]) =>
                        renderField(key, value)
                      )
                    : renderField(`item_${index}`, item)}
                  {index < data.length - 1 && (
                    <Divider
                      style={{
                        margin: "1.25rem 0",
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.06)",
                      }}
                    />
                  )}
                </div>
              ))
            ) : (
              // Handle object data (call_details, member_details)
              <Row gutter={[16, 16]}>
                {Object.entries(data).map(([key, value]) => (
                  <Col xs={24} sm={12} md={12} key={key}>
                    {renderField(key, value)}
                  </Col>
                ))}
              </Row>
            )}
          </div>
        )}
      </Card>
    );
  };

  // Download PDF
  const handleDownloadWhitesheet = async () => {
    if (!callRequestId) return;
    
    setIsDownloading(true);
    const hide = message.loading("Downloading PDF...");
    try {
      const { blob, filename } = await downloadWhitesheet(callRequestId, 'pdf');
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      message.success("AI Call Summary downloaded in PDF");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      message.error("Failed to download PDF");
    } finally {
      setIsDownloading(false);
      hide();
    }
  };

  // Download Excel
  const handleDownloadExcel = async () => {
    if (!callRequestId) return;
    
    setIsDownloading(true);
    const hide = message.loading("Downloading Excel...");
    try {
      const { blob, filename } = await downloadWhitesheet(callRequestId, 'excel');
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      message.success("AI Call Summary downloaded in Excel");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      message.error("Failed to download Excel");
    } finally {
      setIsDownloading(false);
      hide();
    }
  };

  // Copy to clipboard functionality
  const handleCopyToClipboard = () => {
    if (!callSummaryData) return;

    let textToCopy = "Call Summary Report\n" + "=".repeat(20) + "\n\n";

    // Add Member Details
    if (callSummaryData.member_details) {
      textToCopy += "MEMBER DETAILS:\n";
      Object.entries(callSummaryData.member_details).forEach(([key, value]) => {
        const displayValue = safeValueToString(value);
        textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
      });
      textToCopy += "\n";
    }

    // Add Call Details
    if (callSummaryData.call_details) {
      textToCopy += "CALL DETAILS:\n";
      Object.entries(callSummaryData.call_details).forEach(([key, value]) => {
        const displayValue = safeValueToString(value);
        textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
      });
      textToCopy += "\n";
    }

    // Add Detailed Breakdown
    if (callSummaryData.detailed_breakdown) {
      textToCopy += "DETAILED BREAKDOWN:\n";
      const breakdownData = Array.isArray(callSummaryData.detailed_breakdown)
        ? callSummaryData.detailed_breakdown
        : [callSummaryData.detailed_breakdown];

      breakdownData.forEach((item, index) => {
        if (breakdownData.length > 1) {
          textToCopy += `Record ${index + 1}:\n`;
        }
        if (item && typeof item === "object") {
          Object.entries(item).forEach(([key, value]) => {
            const displayValue = safeValueToString(value);
            textToCopy += `${formatFieldName(key)}: ${displayValue}\n`;
          });
        } else {
          textToCopy += `${safeValueToString(item)}\n`;
        }
        textToCopy += "\n";
      });
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopyTooltipOpen(true);
        setTimeout(() => {
          setCopyTooltipOpen(false);
        }, 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  // If no data available
  if (!hasData) {
    return (
      <div
        style={{
          padding: "3.75rem 2.5rem",
          textAlign: "center",
          backgroundColor: isDark ? "#04002b" : "#fafafa",
          borderRadius: "0.75rem",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
          }`,
          boxShadow: isDark
            ? "0 0.25rem 0.75rem rgba(0, 0, 0, 0.3)"
            : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{
            padding: "1.25rem",
            borderRadius: "50%",
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.02)",
            display: "inline-block",
            marginBottom: "1.25rem",
          }}
        >
          <InfoCircleOutlined
            style={{
              fontSize: "3rem",
              color: isDark ? "#666" : "#ccc",
            }}
          />
        </div>
        <Title
          level={4}
          style={{
            color: isDark ? "#ccc" : "#666",
            marginBottom: "0.5rem",
          }}
        >
          No Call Summary Available
        </Title>
        <Text
          style={{
            color: isDark ? "#999" : "#999",
            fontSize: "0.875rem",
          }}
        >
          Call summary data will appear here once the call is processed
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "95%",
        backgroundColor: currentTheme.callhistory.searchBackground,
        borderRadius: "1rem",
        padding: "1.5rem",
        height: "60vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        style={{ marginBottom: "1.5rem", flexShrink: 0 }}
      >
        <div>
          <Title
            level={2}
            style={{
              margin: 0,
              color: isDark ? "#fff" : "#333",
              marginBottom: "0.25rem",
            }}
          >
            Call Summary
          </Title>
          <Text
            style={{
              color: isDark ? "#ccc" : "#666",
              fontSize: "0.875rem",
            }}
          >
            Comprehensive call analysis and breakdown
          </Text>
        </div>

        <Flex align="center" gap={12}>
          {/* Verification Status */}
          {isVerified ? (
            <ThemedChip
              variant="success"
              size="medium"
              customStyle={{
                borderRadius: "0.75rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.375rem",
              }}
            >
              <CheckCircleOutlined style={{ fontSize: "0.875rem" }} />
              Verified
            </ThemedChip>
          ) : (
            <ThemedChip
              variant="warning"
              size="medium"
              customStyle={{
                borderRadius: "0.75rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.375rem",
              }}
            >
              <ExclamationCircleOutlined style={{ fontSize: "0.875rem" }} />
              Not Verified
            </ThemedChip>
          )}

          {/* Copy Button */}
          <Tooltip
            title="Copied to clipboard!"
            open={copyTooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={() => setCopyTooltipOpen(false)}
          >
            <IconButton
              onClick={handleCopyToClipboard}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "0.625rem",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                borderRadius: "0.5rem",
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* PDF Download Button */}
          <Tooltip title="Download AI Call Summary in PDF">
            <IconButton
              onClick={handleDownloadWhitesheet}
              disabled={isDownloading}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "0.625rem",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                borderRadius: "0.5rem",
                opacity: isDownloading ? 0.6 : 1,
              }}
            >
              <img
                src={`/icons/pdf-${isDark ? "dark" : "light"}-icon.png`}
                alt="PDF Download"
                style={{ width: "1.125rem", height: "1.125rem" }}
              />
            </IconButton>
          </Tooltip>

          {/* Excel Download Button */}
          <Tooltip title="Download AI Call Summary in Excel">
            <IconButton
              onClick={handleDownloadExcel}
              disabled={isDownloading}
              style={{
                color: isDark ? "#fff" : "#333",
                padding: "0.625rem",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                borderRadius: "0.5rem",
                opacity: isDownloading ? 0.6 : 1,
              }}
            >
              <img
                src={`/icons/excel-${isDark ? "dark" : "light"}-icon.png`}
                alt="Excel Download"
                style={{ width: "1.125rem", height: "1.125rem" }}
              />
            </IconButton>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Content Sections */}
      <div
        style={{
          flex: 1,
          paddingRight: "0.5rem",
          overflow: "auto",
          minHeight: 0,
          scrollbarWidth: 'thin',
          scrollbarColor: `${currentTheme.callhistory.borderColor} ${currentTheme.callhistory.searchBackground}`,
        }}
        className="call-summary-content-scroll"
      >
        <style jsx>{`
          .call-summary-content-scroll::-webkit-scrollbar {
            width: 0.5rem;
            background: ${currentTheme.callhistory.searchBackground};
          }
          .call-summary-content-scroll::-webkit-scrollbar-track {
            background: ${currentTheme.callhistory.searchBackground};
            border-radius: 0.25rem;
            border: 1px solid ${currentTheme.callhistory.borderColor}20;
          }
          .call-summary-content-scroll::-webkit-scrollbar-thumb {
            background-color: ${currentTheme.callhistory.borderColor};
            border-radius: 0.25rem;
            border: none;
          }
          .call-summary-content-scroll::-webkit-scrollbar-thumb:hover {
            background-color: ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
          }
          .call-summary-content-scroll::-webkit-scrollbar-corner {
            background: ${currentTheme.callhistory.searchBackground};
          }
        `}</style>
        {/* Call Details */}
        {callSummaryData.call_details &&
          renderSection(
            "Call Details",
            callSummaryData.call_details,
            "callDetails",
            <PhoneOutlined />,
            "Status and outcome information for this call"
          )}

        {/* Detailed Breakdown */}
        {callSummaryData.detailed_breakdown &&
          renderSection(
            "Detailed Breakdown",
            callSummaryData.detailed_breakdown,
            "detailedBreakdown",
            <PhoneOutlined />,
            "Comprehensive details and reference information"
          )}
      </div>
    </div>
  );
};

export default CallSummaryView;
